import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";

const MAX_RENDER = 50;

export default function OutletFilterSelect({ outlets = [], value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  const selected = useMemo(
    () => outlets.find((o) => String(o.outlet_id) === String(value)) || null,
    [outlets, value],
  );

  useEffect(() => {
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? outlets.filter((o) => String(o.outlet_name || "").toLowerCase().includes(q))
      : outlets;
    return list.slice(0, MAX_RENDER);
  }, [outlets, query]);

  const pick = (outletId) => {
    onChange(outletId);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="admin-insights-combobox" ref={ref}>
      <div className="admin-insights-combobox-control">
        <Search size={16} className="admin-insights-combobox-icon" />
        <input
          type="text"
          className="admin-insights-combobox-input"
          placeholder={
            selected ? selected.outlet_name : t("admin.insights.filters.allOutlets")
          }
          value={open ? query : selected ? selected.outlet_name : ""}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
        />
        {selected && (
          <button
            type="button"
            className="admin-insights-combobox-clear"
            onClick={() => pick("")}
            aria-label={t("admin.common.clear")}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="admin-insights-combobox-list">
          <button
            type="button"
            className="admin-insights-combobox-item"
            onMouseDown={(event) => {
              event.preventDefault();
              pick("");
            }}
          >
            <span className="admin-insights-combobox-item-name">
              {t("admin.insights.filters.allOutlets")}
            </span>
          </button>
          {filtered.length === 0 ? (
            <div className="admin-insights-combobox-empty">
              {t("admin.insights.filters.noOutlets")}
            </div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.outlet_id}
                type="button"
                className="admin-insights-combobox-item"
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(String(o.outlet_id));
                }}
              >
                <span className="admin-insights-combobox-item-name">{o.outlet_name}</span>
                <span className="admin-insights-combobox-item-count">{o.proof_count}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
